import useAuth from "../../utils/auth"

import { Dialog, styled, Typography } from "@mui/material"
import type { TOSAgreementSchema } from "@phc/common/gen/preferencesGen"
import { useFlags } from "launchdarkly-react-client-sdk"
import type React from "react"
import { useEffect, useRef } from "react"
import { TRUE } from "../../utils/constants"
import { getThirtyDaysFromNowDate } from "../../utils/helpers/dateHelper"
import { extraColors } from "../../utils/theme"
import { useUpdateUserTrial } from "../AccountManagement/hooks/useManageAssetServiceUsers"
import Button from "../Shared/Button"
import {
  useGetUserTermsOfService,
  useUpdateUserTermsOfService,
} from "./hooks/useTermsOfService"
import { ReactComponent as DefaultTOS } from "./markdowns/terms.md"
import { ReactComponent as TrialTOS } from "./markdowns/trial_tos.md"

interface TOSResponseData {
  is_trial_user?: boolean
  user_tos: {
    tos_agreement?: TOSAgreementSchema
    user?: { user_id?: string }
  }
}

// Update these constants when the default or trial terms of service documents are updated
const TRIAL_TOS_DOC_DATE = new Date("2024-01-23")
const TRIAL_TOS_DOC_NAME = "trial_terms_of_service_2024-01-23"
const DEFAULT_TOS_DOC_DATE = new Date("2025-02-25")
const DEFAULT_TOS_DOC_NAME = "default_terms_of_service_2025-02-25"

export const DialogStyled = styled(Dialog)(() => ({
  ".MuiPaper-root": {
    height: "fit-content",
    maxHeight: "100%",
    width: "fit-content",
    maxWidth: 714,
  },
})) as typeof Dialog

const ButtonStyled = styled(Button)({
  width: "fit-content",
  padding: "8px 30px",
  alignSelf: "end",
  marginTop: 30,
})

const ScrollContainer = styled("div")({
  maxHeight: 500,
  overflowY: "scroll",
  "::-webkit-scrollbar": {
    width: 6,
    background: extraColors.subtle,
    borderRadius: 6,
  },
  "::-webkit-scrollbar-thumb": {
    background: extraColors.disabled,
    borderRadius: 6,
    width: 6,
  },
  padding: "10px 30px",
  background: extraColors.light,
  borderRadius: 6,
})

interface TOSDocElements {
  docName: string
  docDate: Date
  docHeader: React.ReactNode
  docMarkdown: React.ReactNode
}

export const TermsOfServiceModal: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading, user: authUser } = useAuth()

  const isFirstTimeUser =
    !!authUser?.["https://thepublichealthco.com/v1/is_first_login"]

  const { data: rawTosData, isLoading: tosDataLoading } =
    useGetUserTermsOfService()
  const tosData = rawTosData as TOSResponseData | undefined
  const docByTrialStatus = getDocByTrialStatus(
    tosData?.is_trial_user ?? undefined
  )
  const { showTrialTermsOfService } = useFlags()

  const { mutate, isPending: tosUpdateIsPending } =
    useUpdateUserTermsOfService()
  const { mutate: updateUserTrial, isPending: trialUpdateIsPending } =
    useUpdateUserTrial()

  const showTOS = getSignatureRequired(
    docByTrialStatus,
    tosData?.user_tos.tos_agreement
  )

  const userConvertedToTrialRef = useRef(false)

  const user = tosData?.user_tos.user
  const userNeedsToBeConvertedToTrial =
    isFirstTimeUser && !tosData?.is_trial_user
  const isLoading =
    authLoading || trialUpdateIsPending || tosDataLoading || tosUpdateIsPending

  const hideTerms =
    !showTrialTermsOfService ||
    !tosData ||
    !showTOS ||
    isLoading ||
    !isAuthenticated ||
    userNeedsToBeConvertedToTrial

  useEffect(() => {
    if (
      localStorage.getItem("userConvertedToTrial") === TRUE ||
      !isAuthenticated ||
      !userNeedsToBeConvertedToTrial ||
      isLoading
    ) {
      return
    }

    userConvertedToTrialRef.current = true
    localStorage.setItem(
      "userConvertedToTrial",
      userConvertedToTrialRef.current.toString()
    )

    const updateUserTrialStatus = () => {
      if (!user?.user_id) return
      const now = new Date()
      const thirtyDaysInFuture = getThirtyDaysFromNowDate(now)
      try {
        updateUserTrial({
          userTrial: {
            user: { user_id: user.user_id },
            is_trial_user: true,
            trial_started_at: now.toISOString(),
            trial_ended_at: thirtyDaysInFuture.toISOString(),
          },
        })
      } catch (_error) {
        userConvertedToTrialRef.current = false
        localStorage.setItem(
          "userConvertedToTrial",
          userConvertedToTrialRef.current.toString()
        )
      }
    }

    updateUserTrialStatus()
  }, [
    isAuthenticated,
    isLoading,
    updateUserTrial,
    user?.user_id,
    userNeedsToBeConvertedToTrial,
  ])

  if (hideTerms) return null

  return (
    <DialogStyled open={showTOS} title="Terms and Conditions">
      {docByTrialStatus.docHeader}
      <ScrollContainer>{docByTrialStatus.docMarkdown}</ScrollContainer>
      <ButtonStyled
        variant="contained"
        title="Accept"
        onClick={() =>
          mutate({
            tosAgreement: {
              tos_created_at: docByTrialStatus.docDate.toISOString(),
              tos_doc_name: docByTrialStatus.docName,
              tos_signed_at: new Date().toISOString(),
            },
          })
        }
        disabled={tosUpdateIsPending}
      >
        Accept
      </ButtonStyled>
    </DialogStyled>
  )
}

function getSignatureRequired(
  docElements: TOSDocElements,
  tos_agreement?: TOSAgreementSchema
) {
  if (!tos_agreement) return true

  const { tos_signed_at, tos_created_at, tos_doc_name } = tos_agreement

  const isMissingTermsFields =
    !tos_signed_at || !tos_created_at || !tos_doc_name

  const created = tos_created_at
  const currentDocumentNotSigned =
    tos_doc_name !== docElements.docName ||
    (created
      ? new Date(created).getTime() !== docElements.docDate.getTime()
      : true)

  if (isMissingTermsFields || currentDocumentNotSigned) return true

  if (new Date(tos_created_at) > new Date(tos_signed_at)) return true

  return false
}

function getDocByTrialStatus(is_trial_user?: boolean): TOSDocElements {
  return is_trial_user
    ? {
        docName: TRIAL_TOS_DOC_NAME,
        docDate: TRIAL_TOS_DOC_DATE,
        docHeader: (
          <>
            <Typography variant="h3Bold" sx={{ color: extraColors.medium }}>
              30 Day Pharos Trial
            </Typography>
            <Typography variant="body1Bold" sx={{ margin: "20px 0" }}>
              Please accept the following terms of use agreement to start your
              evaluation.
            </Typography>
          </>
        ),
        docMarkdown: <TrialTOS />,
      }
    : {
        docName: DEFAULT_TOS_DOC_NAME,
        docDate: DEFAULT_TOS_DOC_DATE,
        docHeader: (
          <>
            <Typography variant="h3Bold" sx={{ color: extraColors.medium }}>
              Terms and Conditions
            </Typography>
            <Typography variant="body1" sx={{ margin: "20px 0" }}>
              We have updated our Terms and Conditions. Please accept them to
              continue.
            </Typography>
            <Typography variant="body1Bold">
              By clicking "Accept," I state that I have read and understood the
              Terms and Conditions.
            </Typography>
          </>
        ),
        docMarkdown: <DefaultTOS />,
      }
}
